/**
 * ShiftDialog Component
 * 
 * Dialog for shifting times and positions of transcript groups.
 * Features:
 * - Time shifts (start/end independently)
 * - Position shifts (left/right independently)
 * - Starting transcript index selection
 * - Real-time preview
 * - Batch operations on transcript groups
 */
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Box,
    Typography,
    FormControlLabel,
    Switch,
    MenuItem,
    Select,
    FormControl,
    InputLabel
} from '@mui/material';
import { userRequest } from '../../requestMethods';

const ShiftDialog = ({ open, group, onClose, onApply, postId, showAlert }) => {
    const [shiftData, setShiftData] = useState({
        timeShift: {
            start: 0,
            end: 0,
            applyStart: false,
            applyEnd: false
        },
        positionShift: {
            left: 0,
            right: 0,
            applyLeft: false,
            applyRight: false
        },
        startFromIndex: 0
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        // Reset form when dialog opens
        if (open) {
            setShiftData({
                timeShift: {
                    start: 0,
                    end: 0,
                    applyStart: false,
                    applyEnd: false
                },
                positionShift: {
                    left: 0,
                    right: 0,
                    applyLeft: false,
                    applyRight: false
                },
                startFromIndex: 0
            });
        }
    }, [open]);

    const handleApplyShift = async () => {
        if (!group) return;

        const hasTimeShift = shiftData.timeShift.applyStart || shiftData.timeShift.applyEnd;
        const hasPositionShift = shiftData.positionShift.applyLeft || shiftData.positionShift.applyRight;
        
        if (!hasTimeShift && !hasPositionShift) {
            showAlert('Please select at least one shift option to apply', 'warning');
            return;
        }

        if (shiftData.startFromIndex >= group.transcripts.length) {
            showAlert('Starting index is out of range', 'error');
            return;
        }

        setLoading(true);
        try {
            // Get transcripts starting from the specified index
            const transcriptsToShift = group.transcripts
                .sort((a, b) => a.index - b.index)
                .slice(shiftData.startFromIndex);

            // Calculate new text group boundaries
            let newGroupStartTime = group.allText.start_seconds;
            let newGroupEndTime = group.allText.end_seconds;
            
            // Apply shifts to get new times for calculating boundaries
            const allTranscriptsAfterShift = group.transcripts.map(transcript => {
                const isInShiftRange = transcriptsToShift.some(t => t._id === transcript._id);
                if (!isInShiftRange) return transcript;
                
                return {
                    ...transcript,
                    start: shiftData.timeShift.applyStart 
                        ? Math.max(0, transcript.start + shiftData.timeShift.start)
                        : transcript.start,
                    end: shiftData.timeShift.applyEnd 
                        ? Math.max(0, transcript.end + shiftData.timeShift.end)
                        : transcript.end
                };
            });
            
            // Calculate new group boundaries
            if (allTranscriptsAfterShift.length > 0) {
                const startTimes = allTranscriptsAfterShift.map(t => t.start);
                const endTimes = allTranscriptsAfterShift.map(t => t.end);
                newGroupStartTime = Math.min(...startTimes);
                newGroupEndTime = Math.max(...endTimes);
            }

            // Apply shifts to selected transcripts
            for (const transcript of transcriptsToShift) {
                const updates = {};
                
                // Apply time shifts
                if (shiftData.timeShift.applyStart) {
                    updates.start = Math.max(0, transcript.start + shiftData.timeShift.start);
                }
                if (shiftData.timeShift.applyEnd) {
                    updates.end = Math.max(0, transcript.end + shiftData.timeShift.end);
                }
                
                // Apply position shifts
                if (shiftData.positionShift.applyLeft || shiftData.positionShift.applyRight) {
                    updates.positions = { ...transcript.positions };
                    
                    if (shiftData.positionShift.applyLeft) {
                        updates.positions.left = Math.max(0, transcript.positions.left + shiftData.positionShift.left);
                    }
                    if (shiftData.positionShift.applyRight) {
                        updates.positions.right = Math.max(0, transcript.positions.right + shiftData.positionShift.right);
                    }
                }
                
                // Add text group boundary update (only send with last transcript to avoid duplicate updates)
                const isLastTranscript = transcript._id === transcriptsToShift[transcriptsToShift.length - 1]._id;
                if (isLastTranscript && (newGroupStartTime !== group.allText.start_seconds || newGroupEndTime !== group.allText.end_seconds)) {
                    updates.textGroupUpdate = {
                        textGroupId: group.allText._id,
                        start_seconds: newGroupStartTime,
                        end_seconds: newGroupEndTime
                    };
                }
                
                // Apply updates to the transcript
                await userRequest.patch(`/contentVideoPost/${postId}/transcripts/${transcript._id}`, updates);
            }
            
            showAlert(
                `Shifts applied successfully to ${transcriptsToShift.length} transcripts starting from index ${shiftData.startFromIndex}`, 
                'success'
            );
            onApply();
            onClose();
        } catch (error) {
            showAlert('Error applying shifts: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    if (!group) return null;

    const sortedTranscripts = group.transcripts.sort((a, b) => a.index - b.index);
    const previewTranscript = sortedTranscripts[shiftData.startFromIndex];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Shift Times & Positions - Text Group
                <Typography variant="body2" color="text.secondary">
                    Apply shifts to transcripts starting from a specific index
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    {/* Starting Index Selection */}
                    <Typography variant="h6" sx={{ mb: 2 }}>Starting Point</Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Start from transcript index</InputLabel>
                                <Select
                                    value={shiftData.startFromIndex}
                                    label="Start from transcript index"
                                    onChange={(e) => setShiftData(prev => ({
                                        ...prev,
                                        startFromIndex: e.target.value
                                    }))}
                                >
                                    {sortedTranscripts.map((transcript, index) => (
                                        <MenuItem key={transcript._id} value={index}>
                                            {index}: {transcript.start}s - {transcript.end}s
                                            {transcript.textContent && ` (${transcript.textContent.slice(0, 30)}...)`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                {sortedTranscripts.length - shiftData.startFromIndex} transcript(s) will be affected
                            </Typography>
                        </Grid>
                    </Grid>

                    {/* Time Shifts Section */}
                    <Typography variant="h6" sx={{ mb: 2 }}>Time Shifts (seconds)</Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={2}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={shiftData.timeShift.applyStart}
                                        onChange={(e) => setShiftData(prev => ({
                                            ...prev,
                                            timeShift: {
                                                ...prev.timeShift,
                                                applyStart: e.target.checked
                                            }
                                        }))}
                                    />
                                }
                                label="Start"
                                labelPlacement="top"
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                label="Start Time Shift"
                                type="number"
                                step="0.1"
                                value={shiftData.timeShift.start}
                                onChange={(e) => setShiftData(prev => ({
                                    ...prev,
                                    timeShift: {
                                        ...prev.timeShift,
                                        start: parseFloat(e.target.value) || 0
                                    }
                                }))}
                                disabled={!shiftData.timeShift.applyStart}
                                helperText="Positive = shift forward, Negative = shift backward"
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={shiftData.timeShift.applyEnd}
                                        onChange={(e) => setShiftData(prev => ({
                                            ...prev,
                                            timeShift: {
                                                ...prev.timeShift,
                                                applyEnd: e.target.checked
                                            }
                                        }))}
                                    />
                                }
                                label="End"
                                labelPlacement="top"
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                label="End Time Shift"
                                type="number"
                                step="0.1"
                                value={shiftData.timeShift.end}
                                onChange={(e) => setShiftData(prev => ({
                                    ...prev,
                                    timeShift: {
                                        ...prev.timeShift,
                                        end: parseFloat(e.target.value) || 0
                                    }
                                }))}
                                disabled={!shiftData.timeShift.applyEnd}
                                helperText="Positive = shift forward, Negative = shift backward"
                            />
                        </Grid>
                    </Grid>

                    {/* Position Shifts Section */}
                    <Typography variant="h6" sx={{ mb: 2 }}>Position Shifts (characters)</Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={2}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={shiftData.positionShift.applyLeft}
                                        onChange={(e) => setShiftData(prev => ({
                                            ...prev,
                                            positionShift: {
                                                ...prev.positionShift,
                                                applyLeft: e.target.checked
                                            }
                                        }))}
                                    />
                                }
                                label="Left"
                                labelPlacement="top"
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                label="Left Position Shift"
                                type="number"
                                value={shiftData.positionShift.left}
                                onChange={(e) => setShiftData(prev => ({
                                    ...prev,
                                    positionShift: {
                                        ...prev.positionShift,
                                        left: parseInt(e.target.value) || 0
                                    }
                                }))}
                                disabled={!shiftData.positionShift.applyLeft}
                                helperText="Positive = shift right, Negative = shift left"
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={shiftData.positionShift.applyRight}
                                        onChange={(e) => setShiftData(prev => ({
                                            ...prev,
                                            positionShift: {
                                                ...prev.positionShift,
                                                applyRight: e.target.checked
                                            }
                                        }))}
                                    />
                                }
                                label="Right"
                                labelPlacement="top"
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                label="Right Position Shift"
                                type="number"
                                value={shiftData.positionShift.right}
                                onChange={(e) => setShiftData(prev => ({
                                    ...prev,
                                    positionShift: {
                                        ...prev.positionShift,
                                        right: parseInt(e.target.value) || 0
                                    }
                                }))}
                                disabled={!shiftData.positionShift.applyRight}
                                helperText="Positive = shift right, Negative = shift left"
                            />
                        </Grid>
                    </Grid>

                    {/* Preview Section */}
                    {previewTranscript && (
                        <Box sx={{ mt: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Preview (Starting transcript at index {shiftData.startFromIndex}):
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Current: Start {previewTranscript.start}s, End {previewTranscript.end}s, 
                                Pos [{previewTranscript.positions?.left}-{previewTranscript.positions?.right}]
                            </Typography>
                            <Typography variant="body2" color="primary">
                                After shift: Start {
                                    shiftData.timeShift.applyStart 
                                        ? Math.max(0, previewTranscript.start + shiftData.timeShift.start)
                                        : previewTranscript.start
                                }s, End {
                                    shiftData.timeShift.applyEnd 
                                        ? Math.max(0, previewTranscript.end + shiftData.timeShift.end)
                                        : previewTranscript.end
                                }s, 
                                Pos [{
                                    shiftData.positionShift.applyLeft 
                                        ? Math.max(0, (previewTranscript.positions?.left || 0) + shiftData.positionShift.left)
                                        : (previewTranscript.positions?.left || 0)
                                }-{
                                    shiftData.positionShift.applyRight 
                                        ? Math.max(0, (previewTranscript.positions?.right || 0) + shiftData.positionShift.right)
                                        : (previewTranscript.positions?.right || 0)
                                }]
                            </Typography>
                            {previewTranscript.textContent && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                                    Text: {previewTranscript.textContent.slice(0, 100)}...
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button 
                    onClick={handleApplyShift} 
                    variant="contained"
                    disabled={loading}
                >
                    {loading ? 'Applying...' : `Apply to ${sortedTranscripts.length - shiftData.startFromIndex} transcript(s)`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ShiftDialog; 